/**
 * Structured data (JSON-LD) for vendor market detail pages.
 * Emits schema.org LocalBusiness plus BreadcrumbList aligned with `PageBreadcrumb` on the route.
 */

import { env } from '@web/src/lib/env.client'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import {
  geoPhraseForLocationDescription,
  plainTextFromRichContent,
  SEO_META_DESCRIPTION_MAX_CHARS,
  sameAsFromWebLinks,
  truncateMetaDescription,
} from '@web/src/lib/seo/seo-common'
import { absoluteUrlForPath, baseUrl } from '@web/src/lib/utilsClient'
import type { VendorMarketCategoryEnum, VendorMarketDetailQuery } from 'types/graphql'

type VendorMarketForJsonLd = NonNullable<VendorMarketDetailQuery['vendorMarket']>

function schemaVendorCategoryLabel(category: VendorMarketCategoryEnum): string {
  switch (category) {
    case 'ADULT_PRODUCTS':
      return 'Adult products vendor'
    case 'APPAREL':
      return 'Apparel vendor'
    case 'AUTOMOTIVE':
      return 'Automotive vendor'
    case 'BEAUTY':
      return 'Beauty vendor'
    case 'ENTERTAINMENT':
      return 'Entertainment vendor'
    case 'EVENTS':
      return 'Events vendor'
    case 'FURNITURE':
      return 'Furniture vendor'
    case 'GENERAL_LABOR':
      return 'General labor vendor'
    case 'HEALTH':
      return 'Health vendor'
    case 'MEDIA':
      return 'Media vendor'
    case 'OTHER':
      return 'Lifestyle vendor'
    case 'PROFESSIONAL_SERVICES':
      return 'Professional services vendor'
    case 'TRADES':
      return 'Trades vendor'
    case 'WELLNESS':
      return 'Wellness vendor'
    default: {
      const _exhaustive: never = category
      return _exhaustive
    }
  }
}

function categoryPhraseForSentence(category: VendorMarketCategoryEnum): string {
  const label = schemaVendorCategoryLabel(category)
  return label.charAt(0).toLowerCase() + label.slice(1)
}

function categorySegmentForSeoTitle(category: VendorMarketCategoryEnum): string {
  switch (category) {
    case 'ADULT_PRODUCTS':
      return 'Adult Products'
    case 'APPAREL':
      return 'Apparel'
    case 'AUTOMOTIVE':
      return 'Automotive'
    case 'BEAUTY':
      return 'Beauty'
    case 'ENTERTAINMENT':
      return 'Entertainment'
    case 'EVENTS':
      return 'Events'
    case 'FURNITURE':
      return 'Furniture'
    case 'GENERAL_LABOR':
      return 'General Labor'
    case 'HEALTH':
      return 'Health'
    case 'MEDIA':
      return 'Media'
    case 'OTHER':
      return 'Vendor'
    case 'PROFESSIONAL_SERVICES':
      return 'Professional Services'
    case 'TRADES':
      return 'Trades'
    case 'WELLNESS':
      return 'Wellness'
    default: {
      const _exhaustive: never = category
      return _exhaustive
    }
  }
}

/**
 * Meta description and JSON-LD `description`: unique per listing; name + category + optional geography,
 * then tagline or plain-text excerpt from the long description.
 */
export function buildVendorMarketDescription(vm: VendorMarketForJsonLd): string {
  const name = vm.name?.trim() || 'Vendor'
  const kind = categoryPhraseForSentence(vm.category)
  const locationExact = !vm.serviceRange
  const geo = vm.location?.trim() ? geoPhraseForLocationDescription(vm.location, locationExact) : ''
  const tagLine = vm.tagLine?.trim()
  const bodyPlain = plainTextFromRichContent(vm.description ?? undefined)

  const base = geo ? `${name} is a ${kind} ${geo}.` : `${name} is a ${kind}.`
  const detail = tagLine || bodyPlain
  const out = detail ? `${base} ${detail}` : base

  if (out.length <= SEO_META_DESCRIPTION_MAX_CHARS) return out

  if (detail) {
    const separatorLen = 1
    const budget = SEO_META_DESCRIPTION_MAX_CHARS - base.length - separatorLen
    if (budget >= 40) {
      const truncatedDetail = truncateMetaDescription(detail, budget)
      return `${base} ${truncatedDetail}`
    }
  }

  return truncateMetaDescription(out, SEO_META_DESCRIPTION_MAX_CHARS)
}

function buildVendorMarketBreadcrumbListJsonLd(vendorName: string, vendorPageUrl: string): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: absoluteUrlForPath('/') },
      { '@type': 'ListItem', position: 2, name: 'Vendor Market', item: absoluteUrlForPath('/vendor-market') },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Discover Vendors',
        item: absoluteUrlForPath('/vendor-market/list'),
      },
      { '@type': 'ListItem', position: 4, name: vendorName, item: vendorPageUrl },
    ],
  }
}

function buildVendorOwnerAuthorJsonLd(owner: NonNullable<VendorMarketForJsonLd['owner']>): Record<string, unknown> {
  const profileUrl = absoluteUrlForPath('/auth/signup')
  const imageUrl = owner.profileMedia?.[0]?.imageUrlMedium ?? owner.coverMediaUrlLarge
  const name = owner.name?.trim() || 'Vendor owner'

  return {
    '@type': 'Person',
    name,
    url: profileUrl,
    ...(imageUrl ? { image: imageUrl } : {}),
  }
}

export function buildVendorMarketPageJsonLd(input: {
  vm: VendorMarketForJsonLd
  description: string
}): Record<string, unknown>[] {
  const { vm, description: vmDescription } = input

  const pagePath = `/vendor-market/${vm.slug ?? vm.id}`
  const url = `${baseUrl()}${pagePath}`
  const entityId = `${url}#vendor`

  const trimmedDescription = vmDescription.trim()
  const authorFromOwner = vm.owner ? buildVendorOwnerAuthorJsonLd(vm.owner) : undefined
  const locationExact = !vm.serviceRange

  let reviewValue = 0
  if (vm.reviewCount && vm.reviewCount > 0 && vm.reviewSum != null) {
    reviewValue = Math.min(Math.max(0, vm.reviewSum / vm.reviewCount), 5.0)
  }

  const mainEntity: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@id': entityId,
    '@type': 'LocalBusiness',
    name: vm.name,
    ...(authorFromOwner && { author: authorFromOwner }),
    ...(trimmedDescription ? { description: trimmedDescription } : {}),
    url,
    category: schemaVendorCategoryLabel(vm.category),
    ...(vm.phone && { telephone: vm.phone }),
    ...(vm.email && { email: vm.email }),
    ...(vm.location && {
      address: {
        '@type': 'PostalAddress',
        addressLocality: vm.location,
      },
    }),
    ...(locationExact &&
      vm.locationLat != null &&
      vm.locationLong != null && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: vm.locationLat,
          longitude: vm.locationLong,
        },
      }),
    ...(vm.attachedMedia &&
      vm.attachedMedia.length > 0 && {
        image: vm.attachedMedia.slice(0, 5).map((media) => media.imageUrlMedium),
      }),
    ...(reviewValue > 0 &&
      vm.reviewCount && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: reviewValue.toFixed(1),
          reviewCount: vm.reviewCount,
          bestRating: '5',
          worstRating: '1',
        },
      }),
  }

  const sameAs = sameAsFromWebLinks(vm.webLinks)
  if (sameAs?.length) {
    mainEntity.sameAs = sameAs.length === 1 ? sameAs[0] : sameAs
  }

  const breadcrumbList = buildVendorMarketBreadcrumbListJsonLd(vm.name.trim() || 'Vendor', url)

  return [mainEntity, breadcrumbList]
}

/** Loader fields used for `<head>` SEO on the vendor market detail route. */
export type VendorMarketPageSeoLoaderInput = {
  vendorMarket?: VendorMarketDetailQuery['vendorMarket'] | null
}

export function vendorMarketPageHeadFromLoaderData(loaderData: VendorMarketPageSeoLoaderInput | null | undefined) {
  const vm = loaderData?.vendorMarket ?? undefined

  if (!vm) {
    return seo({ title: `Vendor | ${env.VITE_APP_NAME}` })
  }

  const vmDescription = buildVendorMarketDescription(vm)
  const jsonLdData = buildVendorMarketPageJsonLd({ vm, description: vmDescription })

  const titleName = vm.name?.trim() || 'Vendor'
  const categoryTitle = categorySegmentForSeoTitle(vm.category)

  return seo({
    title: `${titleName} | ${categoryTitle} | ${env.VITE_APP_NAME}`,
    description: vmDescription,
    author: vm.owner?.name,
    url: `${baseUrl()}/vendor-market/${vm.slug ?? vm.id}`,
    image: vm.attachedMedia?.[0]?.imageUrlMedium,
    robots: robotsMetaForEnvironment({
      production: 'index, follow',
      staging: 'noindex,nofollow',
    }),
    jsonLd: jsonLdData,
  })
}
